import React, { useContext, useCallback } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import CashierApi from "../../utility/CashierApi";
import { CustomerIdContext } from "../../context/CustomerIdProvider";
import { removeFromOfflineCart, isLocalId } from "../../utility/offline/offlineCart";
import { isOffline } from "../../utility/offline/reachability";

function DeleteCartItem({ itemId }) {
  const { deleteData } = CashierApi();
  const { selectedCustomerId } = useContext(CustomerIdContext);
  const queryClient = useQueryClient();

  const deleteCart = useMutation({
    mutationFn: async (id) => {
      const deleteLocally = () =>
        removeFromOfflineCart(
          selectedCustomerId,
          queryClient.getQueryData(["cart", selectedCustomerId]),
          id
        );
      if (isLocalId(id) || isOffline()) return deleteLocally();
      try {
        return await deleteData("carts/delete/", id);
      } catch (error) {
        if (error?.isNetworkError) return deleteLocally();
        throw error;
      }
    },
    retry: false,
    onMutate: async (newCart) => {
      await queryClient.cancelQueries("cart"); // Cancel any outgoing refetches

      const previousCartData = queryClient.getQueryData([
        "cart",
        selectedCustomerId,
      ]);

      queryClient.setQueryData(["cart", selectedCustomerId], (old) => {
        return [...old.filter((x) => x.id !== newCart)];
      }); // Optimistically update the todos

      return { previousCartData };
    },
    onError: (error, newCart, context) => {
      queryClient.setQueryData(
        ["cart", selectedCustomerId],
        context.previousCartData
      );
    },

    onSettled: () => {
      const isCartUpdated = queryClient.getQueryData([
        "cart",
        selectedCustomerId,
      ]);
      if (!isCartUpdated) {
        queryClient.invalidateQueries(["cart", selectedCustomerId]);
      }
    },
  });

  const handleDelete = useCallback(() => {
    deleteCart.mutate(itemId);
  }, [deleteCart, itemId]);

  return (
    <div className="flex justify-center items-center w-full h-full">
      <div className=" p-1 bg-red-600 rounded-md cursor-pointer">
        <DeleteIcon
          sx={{ fontSize: "20px" }}
          onClick={!deleteCart.isPending ? handleDelete : undefined}
          className="text-white "
        />
      </div>
    </div>
  );
}

export default React.memo(DeleteCartItem);
